import { createSelector } from 'reselect';
import { selectAccountsSeparatedDebt } from 'reducers/selectors';
import i18n from 'utils/i18n';
import { sections as sectionLabels } from '../constants';
export const ALL_DETAIL_TABS = [
    'Everyday',
    'Savings',
    'Investments',
    'Assets',
    'Debt',
];
const ALL_TYPE_SECTIONS = [
    { key: 'CHECKING', title: '' }, // filled via i18n at call-site
    { key: 'SAVINGS', title: sectionLabels.savings },
    { key: 'INVESTMENT', title: sectionLabels.investments },
    { key: 'CRYPTO', title: 'Crypto' },
    { key: 'PENSION', title: sectionLabels.pensions },
    { key: 'REAL_ESTATE', title: sectionLabels.realEstate },
    { key: 'VEHICLE', title: sectionLabels.vehicles },
    { key: 'OTHER', title: sectionLabels.otherAssets },
    { key: 'LOAN', title: sectionLabels.loans },
    { key: 'MORTGAGE', title: sectionLabels.mortgages },
    { key: 'CREDITCARD', title: sectionLabels.creditCards },
];
const getSectionTitle = (key) => key === 'CHECKING'
    ? i18n.t('current')
    : ALL_TYPE_SECTIONS.find((s) => s.key === key)?.title ?? key;
const groupByType = (accounts) => {
    const result = [];
    for (const { key } of ALL_TYPE_SECTIONS) {
        const data = accounts.filter((a) => a.type === key);
        if (data.length)
            result.push({ title: getSectionTitle(key), data });
    }
    return result;
};
export const makeSelectOverviewHiddenSections = (title) => createSelector([selectAccountsSeparatedDebt], (accounts) => {
    switch (title) {
        case 'Everyday': {
            const result = [];
            if (accounts.CHECKING_HIDDEN.array.length) {
                result.push({
                    title: i18n.t('current'),
                    data: accounts.CHECKING_HIDDEN.array,
                });
            }
            if (accounts.CREDITCARD_HIDDEN.array.length) {
                result.push({
                    title: 'Credit cards',
                    data: accounts.CREDITCARD_HIDDEN.array,
                });
            }
            return result;
        }
        case 'Savings':
            return accounts.SAVINGS_HIDDEN.array.length
                ? [
                    {
                        title: sectionLabels.savings,
                        data: accounts.SAVINGS_HIDDEN.array,
                    },
                ]
                : [];
        case 'Investments': {
            const result = [];
            const investHidden = accounts.INVESTMENT_HIDDEN.array.filter((a) => a.type === 'INVESTMENT');
            const cryptoHidden = accounts.INVESTMENT_HIDDEN.array.filter((a) => a.type === 'CRYPTO');
            if (investHidden.length) {
                result.push({
                    title: sectionLabels.investments,
                    data: investHidden,
                });
            }
            if (cryptoHidden.length) {
                result.push({ title: 'Crypto', data: cryptoHidden });
            }
            return result;
        }
        case 'Assets':
            return groupByType(accounts.HIDDEN.positiveAccounts);
        case 'Debt':
            return groupByType(accounts.HIDDEN.negativeAccounts);
        default:
            return [];
    }
});
const splitClosedAndHidden = (array) => ({
    visible: array.filter((a) => !a.isHidden),
    hidden: array.filter((a) => a.isHidden),
});
const markFirstHidden = (sections) => sections.map((s, i) => (i === 0 ? { ...s, showHiddenGroupHeader: true } : s));
export const makeSelectOverviewClosedSections = (title) => createSelector([selectAccountsSeparatedDebt], (accounts) => {
    const visibleSections = [];
    const hiddenSections = [];
    switch (title) {
        case 'Everyday': {
            const checking = splitClosedAndHidden(accounts.CHECKING_CLOSED.array);
            const creditCard = splitClosedAndHidden(accounts.CREDITCARD_CLOSED.array);
            if (checking.visible.length) {
                visibleSections.push({
                    title: i18n.t('current'),
                    data: checking.visible,
                });
            }
            if (creditCard.visible.length) {
                visibleSections.push({
                    title: 'Credit cards',
                    data: creditCard.visible,
                });
            }
            if (checking.hidden.length) {
                hiddenSections.push({
                    title: i18n.t('current'),
                    data: checking.hidden,
                });
            }
            if (creditCard.hidden.length) {
                hiddenSections.push({
                    title: 'Credit cards',
                    data: creditCard.hidden,
                });
            }
            break;
        }
        case 'Savings': {
            const savings = splitClosedAndHidden(accounts.SAVINGS_CLOSED.array);
            if (savings.visible.length) {
                visibleSections.push({
                    title: sectionLabels.savings,
                    data: savings.visible,
                });
            }
            if (savings.hidden.length) {
                hiddenSections.push({
                    title: sectionLabels.savings,
                    data: savings.hidden,
                });
            }
            break;
        }
        case 'Investments': {
            const investment = splitClosedAndHidden(accounts.INVESTMENT_CLOSED.array.filter((a) => a.type === 'INVESTMENT'));
            const crypto = splitClosedAndHidden(accounts.INVESTMENT_CLOSED.array.filter((a) => a.type === 'CRYPTO'));
            if (investment.visible.length) {
                visibleSections.push({
                    title: sectionLabels.investments,
                    data: investment.visible,
                });
            }
            if (crypto.visible.length) {
                visibleSections.push({ title: 'Crypto', data: crypto.visible });
            }
            if (investment.hidden.length) {
                hiddenSections.push({
                    title: sectionLabels.investments,
                    data: investment.hidden,
                });
            }
            if (crypto.hidden.length) {
                hiddenSections.push({ title: 'Crypto', data: crypto.hidden });
            }
            break;
        }
        case 'Assets': {
            for (const { key } of ALL_TYPE_SECTIONS) {
                const split = splitClosedAndHidden(accounts.CLOSED.positiveAccounts.filter((a) => a.type === key));
                if (split.visible.length) {
                    visibleSections.push({
                        title: getSectionTitle(key),
                        data: split.visible,
                    });
                }
                if (split.hidden.length) {
                    hiddenSections.push({
                        title: getSectionTitle(key),
                        data: split.hidden,
                    });
                }
            }
            break;
        }
        case 'Debt': {
            for (const { key } of ALL_TYPE_SECTIONS) {
                const split = splitClosedAndHidden(accounts.CLOSED.negativeAccounts.filter((a) => a.type === key));
                if (split.visible.length) {
                    visibleSections.push({
                        title: getSectionTitle(key),
                        data: split.visible,
                    });
                }
                if (split.hidden.length) {
                    hiddenSections.push({
                        title: getSectionTitle(key),
                        data: split.hidden,
                    });
                }
            }
            break;
        }
        default:
            return [];
    }
    return [...visibleSections, ...markFirstHidden(hiddenSections)];
});
export const selectTotalHiddenCount = (sections) => sections.reduce((acc, s) => acc + s.data.length, 0);
export const selectTotalClosedCount = (sections) => sections.reduce((acc, s) => acc + s.data.length, 0);
export const selectHiddenTabAvailability = createSelector([selectAccountsSeparatedDebt], (accounts) => ({
    Everyday: accounts.CHECKING_HIDDEN.array.length > 0 ||
        accounts.CREDITCARD_HIDDEN.array.length > 0,
    Savings: accounts.SAVINGS_HIDDEN.array.length > 0,
    Investments: accounts.INVESTMENT_HIDDEN.array.length > 0,
    Assets: accounts.HIDDEN.positiveAccounts.length > 0,
    Debt: accounts.HIDDEN.negativeAccounts.length > 0,
}));
export const selectClosedTabAvailability = createSelector([selectAccountsSeparatedDebt], (accounts) => ({
    Everyday: accounts.CHECKING_CLOSED.array.length > 0 ||
        accounts.CREDITCARD_CLOSED.array.length > 0,
    Savings: accounts.SAVINGS_CLOSED.array.length > 0,
    Investments: accounts.INVESTMENT_CLOSED.array.length > 0,
    Assets: accounts.CLOSED.positiveAccounts.length > 0,
    Debt: accounts.CLOSED.negativeAccounts.length > 0,
}));
